import type { FileType } from '@atlas/types'
import { Upload } from '@atlas/ui-kit'
import { useNavigate } from 'react-router-dom'
import type { WorkOrder } from '@/domain/work-order.types'
import { packFilesOwner, usePackFiles, useUploadPackFile } from '@/hooks/usePackFiles'
import { useThumbnails } from '@/hooks/useThumbnails'
import { downloadFile, useDeleteWorkOrderFile } from '@/hooks/useWorkOrderFiles'
import { notification } from '@/notifications/notification-store'
import { DeliverableCard } from '../../WorkOrders/DeliverableCard'
import { useSlotUploadDriver } from '../../WorkOrders/workspace/sections/useSlotUploadDriver'

const MAX_BYTES = 50 * 1024 * 1024

/** What a drop zone takes: the MIME types, the extensions they come with, and the hint naming them. */
export interface PackFileKind {
	types: readonly string[]
	/** Lower-case, with the dot; read when the browser reports no type, as it can for Office files. */
	extensions: readonly string[]
	hint: string
}

const DOCUMENT_TYPES = [
	'application/pdf',
	'application/msword',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	'application/vnd.ms-excel',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

export const DOCUMENT_FILES: PackFileKind = {
	types: DOCUMENT_TYPES,
	extensions: ['.pdf', '.doc', '.docx', '.xls', '.xlsx'],
	hint: 'Word, PDF or Excel (Multiple files, up to 50MB each)',
}

export const INSTRUCTION_FILES: PackFileKind = {
	types: ['image/png', 'image/jpeg', 'application/pdf'],
	extensions: ['.png', '.jpg', '.jpeg', '.pdf'],
	hint: 'PNG, JPG or PDF (Multiple files, up to 50MB each)',
}

/** Whether the file is of the kind: by its type, or by its extension when it has no type. */
export function isOfKind(file: File, kind: PackFileKind): boolean {
	if (file.type) return kind.types.includes(file.type)
	const name = file.name.toLowerCase()
	return kind.extensions.some((extension) => name.endsWith(extension))
}

/**
 * The files of one kind the PM attaches for the designer, listed with a delete for whoever may
 * edit, and the drop zone to add more. With a campaign code they are the campaign's files,
 * wherever they were uploaded from.
 */
export function PackFileUploads({
	workOrder: wo,
	fileType,
	campaignCode = null,
	kind,
	label,
	canEdit,
}: {
	workOrder: WorkOrder
	fileType: FileType
	campaignCode?: string | null
	kind: PackFileKind
	label: string
	canEdit: boolean
}) {
	const scope = { orderCode: wo.orderCode, campaignCode }
	const files = usePackFiles(scope, fileType).data ?? []
	const thumbnails = useThumbnails(files)
	const navigate = useNavigate()
	const upload = useUploadPackFile(wo, fileType, campaignCode)
	const del = useDeleteWorkOrderFile(wo.orderCode)
	const { rows, cardProps, uploadAll } = useSlotUploadDriver(
		packFilesOwner(scope, fileType),
		upload.mutateAsync,
		files.map((file) => ({ ...file, thumbnailUrl: thumbnails[file.fileId] ?? undefined })),
	)

	const onFiles = (list: File[]) => {
		const wrongType = list.filter((file) => !isOfKind(file, kind))
		if (wrongType.length) notification.error(`Only ${kind.hint.split(' (')[0]} files are accepted.`)
		const tooBig = list.filter((file) => file.size > MAX_BYTES)
		for (const file of tooBig) notification.error(`${file.name} is over 50MB.`)
		void uploadAll(list.filter((file) => isOfKind(file, kind) && file.size <= MAX_BYTES))
	}

	return (
		<div className="space-y-2">
			{canEdit ? (
				// No accepted types on the zone itself: it would drop a wrong file silently, and the
				// PM is told instead.
				<Upload small aria-label={label} hint={kind.hint} onFilesSelected={onFiles} />
			) : null}
			{rows.length ? (
				<ul className="space-y-2">
					{rows.map((row) => (
						<DeliverableCard
							key={row.key}
							{...cardProps(row)}
							onOpen={(id) => void navigate(`/assets/${id}`)}
							onDownload={(id) => void downloadFile(id)}
							onDelete={canEdit ? (id) => del.mutate(id) : undefined}
						/>
					))}
				</ul>
			) : null}
		</div>
	)
}
